import { create } from 'zustand';
import { LeadService } from '../entities/Lead';
import type { LeadWithPhones } from '../entities/Lead';

export type ActiveLeadStatus = 'idle' | 'looking-up' | 'found' | 'not-found' | 'error';
export type ActiveLeadSource = 'auto' | 'manual' | null;

interface ActiveLeadState {
  lead: LeadWithPhones | null;
  source: ActiveLeadSource;
  status: ActiveLeadStatus;
  lastError: string | null;
  lookupDurationMs: number | null;
  callId: string | null;

  lookupByPhone: (phone: string, callId?: string) => Promise<void>;
  selectManually: (lead: LeadWithPhones) => void;
  clear: () => void;
}

export const useActiveLeadStore = create<ActiveLeadState>(set => ({
  lead: null,
  source: null,
  status: 'idle',
  lastError: null,
  lookupDurationMs: null,
  callId: null,

  lookupByPhone: async (phone, callId) => {
    console.log(`[ACTIVE-LEAD] 🔎 lookupByPhone start — phone=${phone}, callId=${callId ?? '(none)'}`);
    set({ status: 'looking-up', lastError: null, callId: callId ?? null });
    try {
      const r = await LeadService.lookupLive(phone, callId);
      console.log('[ACTIVE-LEAD] 📥 lookupLive response:', {
        found: r.found,
        source: r.source,
        duration_ms: r.duration_ms,
        leadId: r.lead?.id,
        leadName: r.lead?.name,
        externalId: r.lead?.external_id,
      });
      if (r.found && r.lead) {
        console.log(
          `%c[ACTIVE-LEAD] ✅ Lead matched (source=${r.source}) — ${r.lead.name} [${r.lead.id}]`,
          'color:#10b981;font-weight:bold;'
        );
        set({
          lead: r.lead,
          source: 'auto',
          status: 'found',
          lookupDurationMs: r.duration_ms,
        });
      } else {
        console.warn(
          `[ACTIVE-LEAD] ❌ No lead match for phone=${phone} (source=${r.source}, duration=${r.duration_ms}ms)`
        );
        set({
          lead: null,
          source: null,
          status: 'not-found',
          lookupDurationMs: r.duration_ms,
        });
      }
    } catch (e: unknown) {
      console.error('[ACTIVE-LEAD] 💥 lookupByPhone error:', e);
      set({
        status: 'error',
        lastError: e instanceof Error ? e.message : String(e),
      });
    }
  },

  selectManually: (lead) => {
    set({ lead, source: 'manual', status: 'found', lastError: null });
  },

  clear: () => {
    set({
      lead: null,
      source: null,
      status: 'idle',
      lastError: null,
      lookupDurationMs: null,
      callId: null,
    });
  },
}));
